import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import {
  Home, CalendarDays, DoorOpen, Clock, MessageSquare, Globe, User, FileText,
  Gift, Star, CreditCard, PoundSterling, Receipt, BarChart3, Package, Store,
  ScanLine, ShoppingCart, Users, GraduationCap, Pin, Newspaper, BookOpen,
  Camera, TrendingUp, Sparkles, Settings, Shield, Search, Bell, LifeBuoy,
  LogOut, Volume2, Square,
} from 'lucide-react';
import { CeoSalonSwitch } from '@/components/CeoSalonSwitch';
import { CeoAccountSwitch } from '@/components/CeoAccountSwitch';

/* one_menu_01nov2026 (CEO, One_Menu_FINAL_1) — ONE side menu for the whole
   platform. Items come from GET /api/bk/one-menu (filtered by booking role
   and the salon's switches server-side); booking views open the design app
   at /bookapp/index.html#v=<view>. Same plum drawer as menu_reskin. */

const PLUM = '#291525';
const PLUM2 = '#3B2235';
const CREAM = '#F8F4F1';
const GOLD = '#D9BE8E';
const GOLDINK = '#7E5F2E';
const MUTED = '#CDBFC9';

const ICONS = {
  home: Home, cal: CalendarDays, door: DoorOpen, clock: Clock, chat: MessageSquare,
  globe: Globe, user: User, form: FileText, gift: Gift, star: Star, card: CreditCard,
  pound: PoundSterling, bill: Receipt, chart: BarChart3, box: Package, shop: Store,
  scan: ScanLine, cart: ShoppingCart, quote: FileText, team: Users, cap: GraduationCap,
  pin: Pin, news: Newspaper, book: BookOpen, cam: Camera, up: TrendingUp,
  nail: Sparkles, cog: Settings, shield: Shield,
};

const tid = (label) => `nav-${label.toLowerCase().replace(/[&'()\s:]+/g, '-').replace(/-+$/, '')}`;

export const SalonLuxeMenu = ({ open, onClose, menu, onLogout }) => {
  const { user, token } = useAuth();
  const { totalItems } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [unread, setUnread] = useState(0);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => { if (open) setQ(''); }, [open]);
  useEffect(() => () => { window.speechSynthesis?.cancel(); }, []);

  useEffect(() => {
    if (!token) return undefined;
    let dead = false;
    const load = async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-total`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (!dead) setUnread(r.data.total || 0);
      } catch { /* badge stays as-is */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { dead = true; clearInterval(t); };
  }, [token]);

  const groups = menu?.groups || [];
  const needle = q.trim().toLowerCase();
  const visible = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !needle
      || i.label.toLowerCase().includes(needle) || (g.heading || '').toLowerCase().includes(needle)) }))
    .filter((g) => g.items.length);

  const listen = () => {
    const s = window.speechSynthesis;
    if (!s) return;
    if (speaking) { s.cancel(); setSpeaking(false); return; }
    const text = 'Menu. ' + visible.map((g) => (g.heading ? g.heading + ': ' : '')
      + g.items.map((i) => i.label).join(', ')).join('. ')
      + '. At the bottom: notifications, help and F A Q, and log out.';
    s.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-GB';
    u.onend = () => setSpeaking(false);
    s.speak(u);
    setSpeaking(true);
  };

  const goFoot = (path) => { onClose(); navigate(path); };
  const initials = (((user?.first_name || user?.name || '?').charAt(0))
    + ((user?.last_name || '').charAt(0))).toUpperCase();
  const who = menu?.user || {};

  const itemStyle = (on, dashed) => ({
    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
    border: dashed ? '1px dashed #6A4E62' : 0, background: on ? CREAM : 'transparent',
    color: on ? PLUM : CREAM, textAlign: 'left', padding: '0 10px', minHeight: 46,
    borderRadius: 12, fontSize: 16, fontWeight: on ? 600 : 400, textDecoration: 'none',
  });

  return (
    <>
      {open && (
        <div data-testid="drawer-overlay" onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(41,21,37,.45)' }} />
      )}
      <nav aria-label="Menu" data-testid="sidebar"
        style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: 'min(330px,86vw)', zIndex: 85,
          background: PLUM, color: CREAM, display: 'flex', flexDirection: 'column',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          boxShadow: open ? '12px 0 40px rgba(0,0,0,.3)' : 'none',
          transform: open ? 'none' : 'translateX(-105%)', transition: 'transform .25s ease' }}>
        {/* header: gold logo · listen · cream Close */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 16px 12px' }}>
          <Link to="/dashboard" onClick={onClose} aria-label="Revitalise Keratin, home" data-testid="salon-menu-logo"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, textDecoration: 'none' }}>
            <i style={{ width: 16, height: 28, background: GOLD, display: 'block' }} />
            <b style={{ fontWeight: 500, letterSpacing: 3, fontSize: 13, color: CREAM }}>REVITALISE</b>
            <small style={{ fontSize: 9, letterSpacing: 2, color: MUTED, alignSelf: 'flex-end' }}>KERATIN</small>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" onClick={listen} data-testid="salon-menu-listen"
              aria-label={speaking ? 'Stop reading the menu' : 'Listen to the menu'}
              style={{ border: `1px solid ${speaking ? GOLD : '#5A4254'}`, background: speaking ? GOLD : 'transparent',
                color: speaking ? PLUM : GOLD, borderRadius: 14, height: 40, width: 40,
                display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
              {speaking ? <Square size={16} /> : <Volume2 size={18} />}
            </button>
            <button type="button" onClick={onClose} data-testid="drawer-close-btn"
              style={{ border: 0, background: CREAM, color: PLUM, borderRadius: 14, height: 40,
                padding: '0 14px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
        {/* search the menu */}
        <label style={{ margin: '0 16px 10px', display: 'flex', alignItems: 'center', gap: 8,
          background: PLUM2, border: '1px solid #5A4254', borderRadius: 14, padding: '0 12px', height: 44 }}>
          <Search size={18} color={GOLD} aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="salon-menu-search"
            placeholder="Search the menu, e.g. orders" aria-label="Search the menu"
            style={{ flex: 1, minWidth: 0, border: 0, background: 'transparent', color: CREAM,
              font: 'inherit', fontSize: 15, outline: 'none' }} />
        </label>
        {/* groups */}
        <div data-testid="sidebar-nav" style={{ flex: 1, overflowY: 'auto', padding: '0 10px 10px', overscrollBehavior: 'contain' }}>
          {visible.length === 0 && (
            <p data-testid="salon-menu-no-match" style={{ color: MUTED, padding: 10, fontSize: 14 }}>
              {menu ? 'Nothing matches.' : 'Loading the menu…'}
            </p>
          )}
          {visible.map((g) => (
            <div key={g.heading || 'top'}>
              {g.heading && (
                <div data-testid={`menu-group-${g.heading.toLowerCase().replace(/[&\s]+/g, '-')}`}
                  style={{ fontSize: 11, letterSpacing: '.22em', color: GOLD, textTransform: 'uppercase',
                    padding: '14px 10px 6px', fontWeight: 600 }}>
                  {g.heading}
                </div>
              )}
              {g.items.map((it) => {
                const I = ICONS[it.icon] || FileText;
                const badge = it.href === '/dashboard/cart' ? totalItems : 0;
                const to = it.view ? `/dashboard/booking/${it.view}` : it.href;
                const on = location.pathname === to;
                return (
                  <Link key={it.label} to={to} data-testid={tid(it.label)}
                    aria-current={on ? 'page' : undefined}
                    onClick={(e) => { if (location.pathname === to && !it.view) e.preventDefault(); onClose(); }}
                    style={itemStyle(on)}>
                    <I size={22} color={on ? GOLDINK : GOLD} strokeWidth={1.8} style={{ flex: '0 0 22px' }} aria-hidden="true" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
                    {badge > 0 && (
                      <span data-testid="sidebar-cart-badge" style={{ marginLeft: 'auto', background: GOLD, color: PLUM,
                        fontSize: 12, fontWeight: 700, borderRadius: 10, padding: '0 7px', lineHeight: '20px' }}>
                        {badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
        {/* pinned footer — Notifications, Help and FAQ, Log out appear ONCE here */}
        <div data-testid="salon-menu-footer" style={{ borderTop: '1px solid #4A3044', background: '#22111E',
          padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
          display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 46, height: 46, borderRadius: '50%', background: PLUM2,
              border: `2px solid ${GOLD}`, display: 'grid', placeItems: 'center', overflow: 'hidden',
              fontFamily: "'Bodoni Moda', Georgia, serif", color: GOLD, fontSize: 18, flex: '0 0 46px' }}>
              {user?.picture
                ? <img src={user.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : initials}
            </span>
            <span style={{ minWidth: 0 }}>
              <b data-testid="salon-menu-user-name" style={{ display: 'block', fontFamily: "'Bodoni Moda', Georgia, serif",
                fontWeight: 500, fontSize: 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {who.name || user?.name}
              </b>
              <small style={{ color: MUTED, fontSize: 13 }}>
                {(who.role_label || 'Salon owner')}{who.salon_name ? ` · ${who.salon_name}` : ''}
              </small>
            </span>
          </div>
          {menu?.ceo && <CeoSalonSwitch dark />}
          <CeoAccountSwitch dark />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => goFoot('/dashboard/messages')} data-testid="footer-notifications"
              style={{ flex: 1, border: '1px solid #5A4254', background: 'transparent', color: CREAM,
                borderRadius: 12, minHeight: 42, fontSize: 13, display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
              <Bell size={15} color={GOLD} aria-hidden="true" /> Notifications
              {unread > 0 && (
                <span data-testid="footer-notifications-count" style={{ background: GOLD, color: PLUM, borderRadius: 9,
                  padding: '0 6px', fontWeight: 700, fontSize: 12, lineHeight: '18px' }}>{unread}</span>
              )}
            </button>
            <button type="button" onClick={() => goFoot('/dashboard/help')} data-testid="footer-help-faq"
              style={{ flex: 1, border: '1px solid #5A4254', background: 'transparent', color: CREAM,
                borderRadius: 12, minHeight: 42, fontSize: 13, display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
              <LifeBuoy size={15} color={GOLD} aria-hidden="true" /> Help and FAQ
            </button>
          </div>
          <button type="button" onClick={() => { onClose(); onLogout(); }} data-testid="logout-button"
            style={{ border: 0, background: GOLD, color: PLUM, borderRadius: 22, minHeight: 46,
              fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8, cursor: 'pointer' }}>
            <LogOut size={17} aria-hidden="true" /> Log out
          </button>
        </div>
      </nav>
    </>
  );
};
